import DialogUtils from "../DialogUtils.js";
import {ctrlOrCmdBtn, safeText} from "../../js/util/CommonUtils.js";
import AccessLevel from "../../js/entities/AccessLevel.js";
import {getUserIcon} from "../../js/util/UserUtils.js";
import DropdownInputElement from "../DropdownInput/DropdownInputElement.js";
import PermissionRepository from "../../js/repository/PermissionRepository.js";

export default class UpdateAccessDialogHtml {
    /** @type {HTMLDialogElement}  */
    #dialog;
    /** @type {string} */
    #currentUserId;
    #callbacks = {
        onSave: () =>{},
        onPermissionChange: ()=>{},
        onPermissionDelete: ()=>{},
        onPermissionAdd: ()=>{},
        onCheckIsSubjectInList: ()=>{},
        onSubjectClick: ()=>{},
        onSubjectSearch: ()=>{},
    }

    /** @param {function} callback */
    onSaveCb(callback){
        this.#callbacks.onSave = callback;
    }

    /** @param {function} callback */
    onPermissionChangeCb(callback){
        this.#callbacks.onPermissionChange = callback;
    }

    /** @param {function} callback */
    onPermissionDeleteCb(callback){
        this.#callbacks.onPermissionDelete = callback;
    }

    /** @param {function} callback */
    onPermissionAddCb(callback){
        this.#callbacks.onPermissionAdd = callback;
    }

    /** @param {function} callback */
    onCheckIsSubjectInList(callback){
        this.#callbacks.onCheckIsSubjectInList = callback;
    }

    /** @param {function} callback */
    onSubjectClick(callback){
        this.#callbacks.onSubjectClick = callback;
    }

    /** @param {function} callback */
    onSubjectSearch(callback){
        this.#callbacks.onSubjectSearch = callback;
    }

    /** @param {string} currentUserId */
    constructor(currentUserId) {
        this.#currentUserId = currentUserId;
    }

    /**
     * @param {Permission[]} permissions
     * @param {boolean} isBatchPermission needed for creating another buttons
     */
    show(permissions, isBatchPermission) {
        this.#dialog = document.body.insertAdjacentElement("beforeend", this.#createDialogElement(isBatchPermission));
        this.#addPermissions(permissions);
        this.#addListeners();
        this.#dialog.showModal();
        this.#fillNewPermissionInput();
        // We focus save button, otherwise new subject input is focused when dialog load, and user can start to search
        // without click on this input. And in this case we will not have teams of the org.
        this.#getGrantButton().focus();
    }

    remove() {
        this.#dialog.remove();
    }

    /** @param {SidDropdownItem[]} sidItems */
    setDropdownItems(sidItems){
        const elements = [];
        for (const sid of sidItems){
            if (sid.isUserSid())
                elements.push(new DropdownInputElement(sid, this.#userDropdownItemTemplate(sid.subject),
                    !this.#isSubjectInList(sid.subject), sid.subject.name))
            else
                elements.push(new DropdownInputElement(sid, this.#teamDropdownItemTemplate(sid.subject),
                    !this.#isSubjectInList(sid.subject), sid.subject.name))
        }
        this.#getNewSubjectInput().elements = elements;
    }

    /** @param {Permission} permission */
    addNewPermission(permission){
        this.#addPermissionRowAndListeners(permission)
    }

    /** @param isVisible */
    toggleProgressBarVisibility(isVisible){
        this.#getProgressBar().style.display = isVisible ? "block" : "none"
    }

    /** @param {string} actionType */
    #onSave(actionType){
        this.toggleProgressBarVisibility(true)
        // If subject input has value (even if option is not selected, so we check rawValue), first we have to
        // call on permission add callbacks. So we can or add new permission before saving, or show error message in input.
        // If subject input does not have value, we just call on save callbacks
        if (this.#getNewSubjectInput().rawValue.length){
            const isPermissionAdded = this.#onPermissionAdd();
            if (isPermissionAdded) {
                this.#callbacks.onSave(actionType)
            }
        } else {
            this.#callbacks.onSave(actionType)
        }
    }

    /**
     * @param {Permission} permission
     * @param {accessLevel: AccessLevel, displayName: string, title: string} inputOptions
     */
    #onPermissionChange(permission, inputOptions){
        this.#callbacks.onPermissionChange(permission, inputOptions)
    }

    /**
     * @param {Permission} permission
     * @param {HTMLElement} row
     */
    #onPermissionDelete(permission, row){
        this.#callbacks.onPermissionDelete(permission)
        row.remove()
    }

    #onPermissionAdd(){
        const newSubjectInput = this.#getNewSubjectInput()
        // We don't want that input have red label and red input border, when it loses focus (when user just changes
        // permissions and doesn't add new for example). That's why we don't use required attribute and validate input here
        if (!newSubjectInput.value){
            newSubjectInput.errorMessage = newSubjectInput.rawValue ? "You should select option" : "Required";
            return false;
        }
        this.#callbacks.onPermissionAdd(newSubjectInput.value.object, this.#getNewAccessLevelInput().value.accessLevel);
        // If user select Public team, in new access level input there are only 2 possible options,
        // so we want to set all possible options to new access level input after grant permission for Public team
        if (newSubjectInput.value.object.isPublicTeam())
            this.#setAccessLevelOptions(this.#getNewAccessLevelInput())
        newSubjectInput.clearInput();
        this.#onChangeSubjectValue()
        return true;
    }

    /** @param {UserJson|Team} subject */
    #isSubjectInList(subject){
        return this.#callbacks.onCheckIsSubjectInList(subject)
    }

    #onSubjectClick(){
        if (this.#getNewSubjectInput().rawValue.length)
            this.#onSubjectSearch()
        else
            this.#callbacks.onSubjectClick()
    }

    #onSubjectSearch(){
        this.#callbacks.onSubjectSearch(this.#getNewSubjectInput().rawValue)
    }

    // If input has value (even if option is not selected, so we check rawValue),
    // we add 'save-and-grant' attr to button, it is needed to change button text
    #onChangeSubjectValue(){
        const isGrantAttrNeeded = !!this.#getNewSubjectInput().rawValue.length;
        this.#getSaveButtonEl().toggleAttribute('grant-and-save', isGrantAttrNeeded);
        if(this.#getSaveNestedButtonEl())
            this.#getSaveNestedButtonEl().toggleAttribute('grant-and-save', isGrantAttrNeeded)
    }

    /** @param {DropdownInputElement} value */
    #onSubjectOptionSelected(value){
        const newSubjectPermission = value.object;
        let newAccessLevel = this.#getNewAccessLevelInput().value.accessLevel;
        if (newSubjectPermission.isTeamSid() && newSubjectPermission.subject.isPublicTeam()
            && newAccessLevel.meetsOrExceeds(AccessLevel.CREATE)) {
            newSubjectPermission.accessLevel = AccessLevel.READ
        } else if (newSubjectPermission.subject.id === this.#currentUserId){
            newSubjectPermission.accessLevel = AccessLevel.ADMIN
        } else {
            newSubjectPermission.accessLevel = newAccessLevel
        }
        this.#setAccessLevelOptions(this.#getNewAccessLevelInput(), newSubjectPermission)
    }

    /** @param {Permission[]} permissions */
    #addPermissions(permissions){
        permissions.forEach(p => this.#addPermissionRowAndListeners(p));
    }

    /** @returns {HTMLElement} */
    #getSaveButtonEl() {
        return this.#dialog.querySelector('button[data-action="save"]')
    }

    /** @returns {HTMLElement} */
    #getProgressBar() {
        return this.#dialog.querySelector('.progress');
    }

    /** @returns {HTMLElement} */
    #getSaveNestedButtonEl() {
        return this.#dialog.querySelector('button[data-action="save-nested"]')
    }

    /** @returns {HTMLElement} */
    #getPermissionsBlock() {
        return this.#dialog.querySelector('[data-id="permissions-block"]')
    }

    /** @returns {SelectInput} */
    #getNewAccessLevelInput() {
        return this.#dialog.querySelector('[data-id="new-access-level"]')
    }

    /** @returns {DropdownInput} */
    #getNewSubjectInput() {
        return this.#dialog.querySelector('[data-id="new-subject"]')
    }

    /** @returns {HTMLButtonElement} */
    #getGrantButton() {
        return this.#dialog.querySelector('[data-id="grant"]')
    }

    #addListeners() {
        this.#getSaveButtonEl().addEventListener('click', this.#onSave.bind(this, PermissionRepository.SAVE_ACTION_TYPE.SAVE));
        if (this.#getSaveNestedButtonEl())
            this.#getSaveNestedButtonEl().addEventListener('click', this.#onSave.bind(this, PermissionRepository.SAVE_ACTION_TYPE.SAVE_NESTED));
        this.#dialog.addEventListener('cancel', this.remove.bind(this));
        const subjectInput = this.#getNewSubjectInput()
        subjectInput.addEventListener('click', this.#onSubjectClick.bind(this));
        subjectInput.addEventListener('input', this.#onSubjectSearch.bind(this));
        subjectInput.addEventListener('input', this.#onChangeSubjectValue.bind(this));
        subjectInput.onOptionSelected(this.#onSubjectOptionSelected.bind(this));
        subjectInput.onChangeValue(this.#onChangeSubjectValue.bind(this));
        this.#getGrantButton().addEventListener('click', this.#onPermissionAdd.bind(this));
        this.#dialog.addEventListener('keydown', (evt)=>{
            if((evt.ctrlKey || evt.metaKey) && evt.key === "Enter" && !evt.repeat){
                evt.stopImmediatePropagation();
                this.#onPermissionAdd();
            }
        })
        DialogUtils.setDefaultDialogListeners(this.#dialog);
    }

    /**
     * @param {SelectInput} input
     * @param {Permission?} permission
     */
    #setAccessLevelOptions(input, permission){
        input.comparator = null;
        // Set possible options to input, from which user can later select the access level
        // Different sids have different possible options
        if (permission && permission.isTeamSid() && permission.subject.isPublicTeam()) {
            // Public team can only read and find resource
            input.options = [
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.READ.intValue()),
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.FIND.intValue()),
            ]
        } else if (permission && permission.subject.id === this.#currentUserId){
            // Current user can choose only Admin access level for himself, so we add only one possible option
            // with accessLevel ADMIN
            // Otherwise he can lower his own permissions and lose access to resource at all
            input.options = [
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.ADMIN.intValue()),
            ]
        } else {
            input.options = [
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.READ.intValue()),
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.FIND.intValue()),
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.WRITE.intValue()),
                UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.ADMIN.intValue()),
            ]
        }
        // Chose and display option for this input
        if (permission){
            if (permission.accessLevel.intValue() !== AccessLevel.NONE.intValue()) {
                input.value = UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(permission.accessLevel.intValue());
            } else {
                input.setPlaceholder("Permissions differ")
            }
        } else {
            // first time when we show the dialog with the search input, we default to READ for any new SID
            input.value = UpdateAccessDialogHtml.#ACCESS_LEVEL_INPUT_OPTIONS.get(AccessLevel.READ.intValue())
        }
    }

    #fillNewPermissionInput() {
        this.#setAccessLevelOptions(this.#getNewAccessLevelInput())
    }

    /** @param {Permission} permission */
    #addPermissionRowAndListeners(permission){
        const permissionBlock = this.#getPermissionsBlock();
        permissionBlock.insertAdjacentHTML('beforeend', this.#permissionRowTemplate(permission));
        const row = permissionBlock.lastElementChild;
        const input = row.querySelector('[data-id="access-level"]');
        this.#setAccessLevelOptions(input, permission);
        input.onChangeValue(this.#onPermissionChange.bind(this, permission));
        const deleteBtn = row.querySelector('[data-id="delete"]');
        if (deleteBtn)
            deleteBtn.addEventListener('click', this.#onPermissionDelete.bind(this, permission, row));
    }

    /**
     * @param {UserJson} user
     * @return {string}
     */
    #userDropdownItemTemplate (user){
        let descriptionMessage = "";
        if (!user.inOrg)
            descriptionMessage = UpdateAccessDialogHtml.DESCRIPTION_IN_DROPDOWN_ITEMS.NOT_IN_ORG;
        if (this.#isSubjectInList(user))
            descriptionMessage = UpdateAccessDialogHtml.DESCRIPTION_IN_DROPDOWN_ITEMS.ALREADY_IN_LIST;

        return `<li data-dropdown-user-id="${user.id}" class="update-access-dialog__dropdown-item">
                    ${getUserIcon(user, 'update-access-dialog__dropdown-user-icon update-access-dialog__icon')}
                    <div class="update-access-dialog__dropdown-item-body">
                        <div>
                            <span class="update-access-dialog__dropdown-username">${safeText(user.name)}</span>
                            <span class="update-access-dialog__dropdown-user-full-name">${safeText(user.fullName)}</span>
                        </div>
                        <div class="update-access-dialog__item-description">${descriptionMessage}</div>
                    </div>
                </li>`
    }

    /**
     * @param {Team} team
     * @return {string}
     */
    #teamDropdownItemTemplate(team) {
        const descriptionMessage = this.#isSubjectInList(team) ? UpdateAccessDialogHtml.DESCRIPTION_IN_DROPDOWN_ITEMS.ALREADY_IN_LIST : "";
        const titleTemplate = team.isPublicTeam() ?
            `title="${UpdateAccessDialogHtml.TITLE_FOR_PUBLIC_TEAM_DROPDOWN_ITEM}"` : '';

        return `<li data-dropdown-team-id="${team.id}" class="update-access-dialog__dropdown-item" ${titleTemplate}>
                    ${UpdateAccessDialogHtml.#TEAM_ICON_TEMPLATE}
                    <div class="update-access-dialog__dropdown-item-body">
                        <div>
                            <span class="update-access-dialog__team-name">${safeText(team.name)}</span>
                        </div>
                        <div class="update-access-dialog__item-description">${descriptionMessage}</div>
                    </div>
                </li>`
    }

    /**
     * @param {boolean} isBatchPermission
     * @returns {HTMLDialogElement}
     */
    #createDialogElement(isBatchPermission) {
        const el = document.createElement("dialog");
        el.classList.add('update-access-dialog');
        el.innerHTML = this.#template(isBatchPermission);
        return el;
    }

    /**
     * @param {Permission} permission
     * @returns {string}
     */
    #permissionRowTemplate(permission){
        // Don't add delete button if it is current user row, because current user cannot delete his own permissions
        let deleteBtnTemplate = this.#currentUserId === permission.subject.id ? '' :
            '<span data-id="delete" class="material-symbols-outlined update-access-dialog__button update-access-dialog__delete-btn">delete</span>'

        const subjectTemplate = permission.isUserSid()
            ? ` ${getUserIcon(permission.subject, "update-access-dialog__icon")}
                <span class="update-access-dialog__user-full-name" title="${safeText(permission.subject.fullName)} (${safeText(permission.subject.name)})">${safeText(permission.subject.fullName)}</span>
                <span class="update-access-dialog__username">(${safeText(permission.subject.name)})</span>`
            : ` ${UpdateAccessDialogHtml.#TEAM_ICON_TEMPLATE}
                <span class="update-access-dialog__subject-text" title="${safeText(permission.name)}">${safeText(permission.name)}</span>`


        return `<div class="update-access-dialog__row update-access-dialog__permission-row" data-id="${permission.sid}">
                    <div class="update-access-dialog__subject-name update-access-dialog__name-block">
                        ${subjectTemplate}
                    </div>
                    <select-input data-id="access-level" label="Permission" class="update-access-dialog__access"></select-input>
                    <div class="update-access-dialog__button">
                        ${deleteBtnTemplate}
                    </div>
                </div>`
    }

    /** @returns {string} */
    #buttonsForBatchTemplate(){
        return `
                <button data-action="save-nested" class="button button--contained btn-ok update-access-dialog__save-button" title="Same sharing settings will be applied to the injections inside">
                    <span class="update-access-dialog__save-button-text--save">Apply to Batch & Injections</span>
                    <span class="update-access-dialog__save-button-text--save-and-grant">Grant & Apply to Batch & Injections</span>
                </button>
                <button data-action="save" class="button button--outlined update-access-dialog__save-button" title="Won’t apply the changes to the nested Injections">
                    <span class="update-access-dialog__save-button-text--save">Apply to Batch</span>
                    <span class="update-access-dialog__save-button-text--save-and-grant">Grant & Apply to Batch</span>
                </button>
        `
    }

    /** @returns {string} */
    #buttonsForInjectionsTemplate(){
        return `
                <button data-action="save" class="button button--contained btn-ok update-access-dialog__save-button">
                    <span class="update-access-dialog__save-button-text--save">Apply</span>
                    <span class="update-access-dialog__save-button-text--save-and-grant">Grant & Apply</span>
                </button>
        `
    }

    /**
     * @param {boolean} isBatchPermission
     * @returns {string}
     */
    #template(isBatchPermission) {
        return `
            <div class="progress">
                <div class="indeterminate"></div>
            </div>
            <div class="update-access-dialog__content dialog-content">
                <div class="update-access-dialog__find-block">
                    <div class="update-access-dialog__row">
                        <div class="update-access-dialog__name-block">
                            <dropdown-input data-id="new-subject" label="Teams or individuals" type="text" placeholder="Find people and teams"></dropdown-input>
                        </div>
                        <select-input data-id="new-access-level" label="Permission" class="update-access-dialog__access"></select-input>
                        <button data-id="grant" title="Press ${ctrlOrCmdBtn()} + Enter to grant permission" class="button button--outlined update-access-dialog__button update-access-dialog__grant-btn">Grant</button>
                    </div>
                </div>
                <div data-id="permissions-block" class="update-access-dialog__permissions-block"></div>
            </div>
            <div class="update-access-dialog__buttons dialog-buttons">
                ${isBatchPermission ? this.#buttonsForBatchTemplate() : this.#buttonsForInjectionsTemplate()} 
                <button data-action="cancel" class="button button--text btn-cancel">Cancel</button>
            </div>
       `
    }

    static #ACCESS_LEVEL_INPUT_OPTIONS = new Map ([
        [AccessLevel.READ.intValue(), {accessLevel: AccessLevel.READ, displayName: "View using a direct link", title: "Can't find the resources using search functionality. Such resources are visible only via direct link or as part of a Batch."}],
        [AccessLevel.FIND.intValue(), {accessLevel: AccessLevel.FIND, displayName: "Find & view", title: "Can find and open"}],
        [AccessLevel.WRITE.intValue(), {accessLevel: AccessLevel.WRITE, displayName: "Edit", title: "Can introduce changes"}],
        [AccessLevel.ADMIN.intValue(), {accessLevel: AccessLevel.ADMIN, displayName: "Administer", title: "Can delete and change permissions"}],
    ])

    static #TEAM_ICON_TEMPLATE = '<div class="update-access-dialog__icon update-access-dialog__team-icon"><span class="material-symbols-outlined">group</span></div>'

    static TITLE_FOR_PUBLIC_TEAM_DROPDOWN_ITEM = "If modifying permissions are granted to everyone, anonymous users still won't be able to introduce changes. Only registered users will be able to";

    static DESCRIPTION_IN_DROPDOWN_ITEMS = {
        ALREADY_IN_LIST: "Already on the list",
        NOT_IN_ORG: "Not part of current org"
    }
}
